// Cliente HTTP com refresh automatico + lock global.
// Espelha apps/web/js/api-client.js (vanilla) - invariantes preservadas:
// - withCredentials/credentials: 'include' (cookie httpOnly frotas_refresh)
// - pendingRefresh singleton (N 401 concorrentes => 1 refresh)
// - performRefresh usa fetch direto, NAO axios (evita loop pelo interceptor)
// - 401 em path no-refresh-list nao tenta refresh
// - dispatchForcedLogout dispara evento que AuthContext escuta pra navegar
//
// Stack: Axios (oficial do time, amendment 1). API publica request<T> mantida
// pra services nao precisarem mudar.
//
// Base URL:
// - Dev (vite dev server): proxy /v1 -> localhost:3100 (vite.config.ts)
// - Container nginx: bundle injeta VITE_API_BASE_URL via .env.production
//   apontando pra http://frotas-api.localhost/v1.

import axios, {
  AxiosHeaders,
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { authStorage } from './auth-storage';
import { getDeviceFingerprint } from './device-fingerprint';

// Type augmentation: flags internas que viajam no config pra interceptor saber
// se deve pular auth e detectar retry.
declare module 'axios' {
  export interface AxiosRequestConfig {
    _skipAuth?: boolean;
    _retried?: boolean;
  }
  export interface InternalAxiosRequestConfig {
    _skipAuth?: boolean;
    _retried?: boolean;
  }
}

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/v1').replace(/\/$/, '');

const NO_REFRESH_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
];

function isNoRefreshPath(path: string): boolean {
  return NO_REFRESH_PATHS.some((p) => path === p || path.endsWith(p));
}

interface ApiProblem {
  status?: number;
  error?: string;
  title?: string;
  detail?: string;
  fields?: Record<string, string>;
  traceId?: string;
  challengeToken?: string;
  expiresIn?: number;
  [key: string]: unknown;
}

export class ApiError extends Error {
  status: number;
  code: string;
  detail: string;
  fields?: Record<string, string>;
  traceId?: string;
  payload: ApiProblem;
  challengeToken?: string;
  expiresIn?: number;

  constructor(problem: ApiProblem) {
    super(problem.detail || problem.title || problem.error || 'Erro');
    this.name = 'ApiError';
    this.status = problem.status ?? 0;
    this.code = problem.error ?? 'ERR_UNKNOWN';
    this.detail = problem.detail || problem.title || problem.error || 'Erro';
    this.fields = problem.fields;
    this.traceId = problem.traceId;
    this.payload = problem;
    if (problem.challengeToken) this.challengeToken = problem.challengeToken;
    if (problem.expiresIn) this.expiresIn = problem.expiresIn;
  }
}

let pendingRefresh: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  if (pendingRefresh) return pendingRefresh;

  pendingRefresh = (async () => {
    try {
      // Fetch direto: nao queremos que esta chamada passe pelo interceptor
      // (que entraria em loop ao receber 401 de refresh ja expirado).
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return false;
      const ct = response.headers.get('content-type') || '';
      if (!ct.includes('application/json')) return false;
      const body = (await response.json().catch(() => null)) as { accessToken?: unknown } | null;
      if (!body || typeof body.accessToken !== 'string' || !body.accessToken) return false;
      authStorage.setToken(body.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      pendingRefresh = null;
    }
  })();

  return pendingRefresh;
}

function dispatchForcedLogout(): void {
  authStorage.clearToken();
  try {
    window.dispatchEvent(new CustomEvent('auth:logout-forced'));
  } catch {
    /* ambiente nao-DOM: ignorar */
  }
}

// Instancia axios compartilhada. Sem header default Authorization: a request()
// adiciona por chamada, ja respeitando opts.auth.
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

// Interceptor de erro: refresh lock + retry + conversao pra ApiError.
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as InternalAxiosRequestConfig | undefined;
    const status = error.response?.status;

    if (
      status === 401 &&
      config &&
      !config._skipAuth &&
      !config._retried &&
      !isNoRefreshPath(config.url || '')
    ) {
      const ok = await performRefresh();
      if (ok) {
        config._retried = true;
        const token = authStorage.getToken();
        if (token) {
          const headers = AxiosHeaders.from(config.headers ?? {});
          headers.set('Authorization', `Bearer ${token}`);
          config.headers = headers;
        }
        return axiosInstance.request(config);
      }
      dispatchForcedLogout();
    }

    // Converte erro Axios em ApiError compativel com o vanilla.
    if (!error.response) {
      throw new ApiError({
        status: 0,
        error: 'ERR_NETWORK',
        title: 'Sem conexão',
        detail: 'Não foi possível conectar. Verifique sua rede.',
      });
    }
    const data = error.response.data;
    if (data && typeof data === 'object' && 'error' in data) {
      throw new ApiError(data as ApiProblem);
    }
    throw new ApiError({
      status: error.response.status,
      error: 'ERR_UNKNOWN',
      title: 'Erro',
      detail: `Erro HTTP ${error.response.status}`,
    });
  },
);

export interface RequestOptions {
  method?: AxiosRequestConfig['method'];
  body?: unknown;
  headers?: Record<string, string>;
  auth?: boolean;
  signal?: AbortSignal;
  responseType?: AxiosRequestConfig['responseType'];
}

export async function request<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, headers = {}, auth = true, method, signal, responseType } = options;
  const finalHeaders: Record<string, string> = { ...headers };
  // FormData: axios seta Content-Type com boundary correto automaticamente.
  // JSON: setamos explicitamente.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !isFormData) finalHeaders['Content-Type'] = 'application/json';
  if (auth) {
    const token = authStorage.getToken();
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
  }
  const fp = getDeviceFingerprint();
  if (fp) finalHeaders['X-Device-Fingerprint'] = fp;

  const finalMethod = method ?? (body !== undefined ? 'POST' : 'GET');

  const resp: AxiosResponse<T> = await axiosInstance.request<T>({
    url: path,
    method: finalMethod,
    headers: finalHeaders,
    data: body,
    signal,
    responseType,
    _skipAuth: !auth,
  });
  return resp.data;
}

export function buildQueryString(params?: Record<string, unknown>): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
    } else {
      search.append(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const apiClient = {
  request,
  buildQueryString,
  baseUrl: API_BASE_URL,
};
